import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// ── Response types (mirror /api/marketing-suite/monthly-leads) ───────
interface Bucket {
  key: string;
  label: string;
  sources: string[];
  goals: Record<string, number | null>;
  actual: Record<string, number>;
  leads: number;
  dealsClosed: number;
  closeRate: number | null;
  goalTotal: number;
}
interface SocialRow {
  key: string;
  label: string;
  derived: boolean;
  actual: Record<string, number>;
  leads: number;
  dealsClosed: number;
  closeRate: number | null;
}
interface CampusRow {
  key: string;
  campus: string;
  y2025: number;
  y2026: number;
}
interface MonthlyLeadsResponse {
  fiscalYearLabel: string;
  currentMonth: string;
  q5Months: string[];
  fyMonths: string[];
  months: string[];
  buckets: Bucket[];
  totals: {
    actualByMonth: Record<string, number>;
    priorYear: Record<string, number | null>;
    growth: Record<string, number | null>;
    goalTotalFY: number;
    leads: number;
    dealsClosed: number;
  };
  social: SocialRow[];
  ddTiktokGoals: Record<string, number | null>;
  ddTiktokGoalKey: string;
  campus: CampusRow[];
  goalBrand: string;
  goalPlatform: string;
  editable: boolean;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const fmtPct = (n: number | null) => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);
function fmtMonth(month: string) {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}
const sumOver = (map: Record<string, number> | undefined, keys: string[]) =>
  keys.reduce((s, k) => s + (map?.[k] ?? 0), 0);

// Shared cell classes.
const TH = "text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap";
const TD = "text-right py-2 px-2 tabular-nums whitespace-nowrap";
const NAME_TH = "sticky left-0 z-10 bg-card text-left py-2 pr-4 font-medium text-muted-foreground";
const NAME_TD = "sticky left-0 z-10 bg-card py-2 pr-4 font-medium whitespace-nowrap";

// An editable goal cell — saves to marketing_metric on blur / Enter.
function GoalCell({
  brand,
  platform,
  metricKey,
  month,
  initial,
  onSaved,
}: {
  brand: string;
  platform: string;
  metricKey: string;
  month: string;
  initial: number | null;
  onSaved: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const save = async (raw: string) => {
    const trimmed = raw.trim();
    const value = trimmed === "" ? 0 : Number(trimmed);
    if (!Number.isFinite(value) || value === (initial ?? 0)) return;
    const res = await fetch("/api/marketing-suite/metric", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        periodType: "month",
        period: month,
        brand,
        platform,
        metricKey,
        value,
      }),
    });
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      onSaved();
    }
  };
  return (
    <input
      type="number"
      defaultValue={initial ?? ""}
      onBlur={(e) => save(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={cn(
        "w-16 rounded border bg-background px-1 py-0.5 text-right tabular-nums text-sm outline-none focus:border-brand-gold",
        saved && "border-green-500 ring-1 ring-green-500/30",
      )}
    />
  );
}

export function MonthlyLeads() {
  const [data, setData] = useState<MonthlyLeadsResponse | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    fetch("/api/marketing-suite/monthly-leads", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load the monthly lead generation table.
        </CardContent>
      </Card>
    );
  }
  if (!data) return <Skeleton className="h-96 w-full" />;

  const { q5Months, fyMonths, months } = data;

  return (
    <div className="space-y-6">
      <ActualTable data={data} />
      <GoalTable data={data} onSaved={load} />
      <SocialTable data={data} />
      <div className="grid gap-6 lg:grid-cols-2">
        <CampusTable data={data} />
        <TikTokGoalTable data={data} onSaved={load} />
      </div>
      <p className="text-xs text-muted-foreground">
        Mirrors the “2026-27 Monthly” tab of the FP Master Marketing spreadsheet. <b>Actuals</b>{" "}
        (leads, deals closed, close rate) are pulled live from HubSpot by lead source — leads =
        quote forms filled, deals closed = orders placed. <b>Goals</b> and the campus figures are
        entered manually. “Q5” = the Jan–Mar {q5Months[0]?.slice(0, 4)} stub before the fiscal year;
        “Total” spans all {months.length} months shown, while the goal total covers the{" "}
        {fyMonths.length} fiscal-year months.
      </p>
    </div>
  );
}

// ── Actual (live from HubSpot) ───────────────────────────────────────
function ActualTable({ data }: { data: MonthlyLeadsResponse }) {
  const { q5Months, fyMonths, months, buckets, totals } = data;
  const colTotal = (map: Record<string, number> | undefined) => sumOver(map, months);

  return (
    <Card>
      <CardHeader>
        <CardTitle>📈 Lead Generation — Actual ({data.fiscalYearLabel})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className={NAME_TH}>Source</th>
                {q5Months.map((m) => (
                  <th key={m} className={TH}>
                    {fmtMonth(m)}
                  </th>
                ))}
                <th className={cn(TH, "border-l text-brand-gold")}>Q5</th>
                {fyMonths.map((m) => (
                  <th key={m} className={cn(TH, "border-l")}>
                    {fmtMonth(m)}
                  </th>
                ))}
                <th className={cn(TH, "border-l")}>Total</th>
                <th className={cn(TH, "border-l")}>Deals Closed</th>
                <th className={TH}>Close Rate</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.key} className="border-b">
                  <td className={NAME_TD}>{b.label}</td>
                  {q5Months.map((m) => (
                    <td key={m} className={TD}>
                      {fmtInt(b.actual[m] ?? 0)}
                    </td>
                  ))}
                  <td className={cn(TD, "border-l font-medium")}>
                    {fmtInt(sumOver(b.actual, q5Months))}
                  </td>
                  {fyMonths.map((m) => (
                    <td
                      key={m}
                      className={cn(
                        TD,
                        "border-l",
                        goalClass(b.actual[m] ?? 0, b.goals[m], m, data.currentMonth),
                      )}
                    >
                      {fmtInt(b.actual[m] ?? 0)}
                    </td>
                  ))}
                  <td className={cn(TD, "border-l font-semibold")}>{fmtInt(b.leads)}</td>
                  <td className={cn(TD, "border-l")}>{fmtInt(b.dealsClosed)}</td>
                  <td className={TD}>{fmtPct(b.closeRate)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className={NAME_TD}>Total</td>
                {q5Months.map((m) => (
                  <td key={m} className={TD}>
                    {fmtInt(totals.actualByMonth[m] ?? 0)}
                  </td>
                ))}
                <td className={cn(TD, "border-l")}>
                  {fmtInt(sumOver(totals.actualByMonth, q5Months))}
                </td>
                {fyMonths.map((m) => (
                  <td key={m} className={cn(TD, "border-l")}>
                    {fmtInt(totals.actualByMonth[m] ?? 0)}
                  </td>
                ))}
                <td className={cn(TD, "border-l")}>{fmtInt(colTotal(totals.actualByMonth))}</td>
                <td className={cn(TD, "border-l")}>{fmtInt(totals.dealsClosed)}</td>
                <td className={TD}>
                  {fmtPct(totals.leads > 0 ? totals.dealsClosed / totals.leads : null)}
                </td>
              </tr>
              <tr className="text-muted-foreground">
                <td className={NAME_TD}>Prior Year</td>
                {q5Months.map((m) => (
                  <td key={m} className={TD}>
                    {data.totals.priorYear[m] == null
                      ? "—"
                      : fmtInt(data.totals.priorYear[m] as number)}
                  </td>
                ))}
                <td className="border-l" aria-hidden />
                {fyMonths.map((m) => (
                  <td key={m} className={cn(TD, "border-l")}>
                    {data.totals.priorYear[m] == null
                      ? "—"
                      : fmtInt(data.totals.priorYear[m] as number)}
                  </td>
                ))}
                <td className="border-l" colSpan={3} aria-hidden />
              </tr>
              <tr className="text-xs text-muted-foreground">
                <td className={NAME_TD}>YoY Growth</td>
                {q5Months.map((m) => (
                  <td key={m} className={cn(TD, growthClass(data.totals.growth[m]))}>
                    {fmtGrowth(data.totals.growth[m])}
                  </td>
                ))}
                <td className="border-l" aria-hidden />
                {fyMonths.map((m) => (
                  <td key={m} className={cn(TD, "border-l", growthClass(data.totals.growth[m]))}>
                    {fmtGrowth(data.totals.growth[m])}
                  </td>
                ))}
                <td className="border-l" colSpan={3} aria-hidden />
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Each completed month is colored vs its goal:{" "}
          <span className="font-medium text-green-600">green</span> met or beat it,{" "}
          <span className="font-medium text-amber-600">amber</span> within 20%,{" "}
          <span className="font-medium text-red-600">red</span> below. The current and upcoming
          months aren't colored until they finish.
        </p>
      </CardContent>
    </Card>
  );
}

function fmtGrowth(n: number | null | undefined) {
  if (n == null) return "—";
  const pct = (n * 100).toFixed(0);
  return `${n >= 0 ? "+" : ""}${pct}%`;
}
function growthClass(n: number | null | undefined) {
  if (n == null) return "";
  return n >= 0 ? "text-green-600" : "text-red-600";
}

// Color an actual number vs its month's goal — but only for months that have
// fully elapsed (an in-progress month shouldn't show red for being partial).
// Green = met/beat goal, amber = within 20%, red = below. No goal → no color.
function goalClass(
  actual: number,
  goal: number | null | undefined,
  month: string,
  currentMonth: string,
) {
  if (goal == null || goal <= 0 || month >= currentMonth) return "";
  if (actual >= goal) return "text-green-600 font-medium";
  if (actual >= goal * 0.8) return "text-amber-600 font-medium";
  return "text-red-600 font-medium";
}

// ── Goals (manual entry) ─────────────────────────────────────────────
function GoalTable({ data, onSaved }: { data: MonthlyLeadsResponse; onSaved: () => void }) {
  const { fyMonths, buckets, editable } = data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>🎯 Lead Generation — Goals ({data.fiscalYearLabel})</CardTitle>
      </CardHeader>
      <CardContent>
        {editable && (
          <p className="mb-3 text-xs text-muted-foreground">
            ✏️ Manual entry — click any month to set that source's goal; it saves automatically.
            Blank months fall back to the spreadsheet defaults.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className={NAME_TH}>Source</th>
                {fyMonths.map((m) => (
                  <th key={m} className={cn(TH, "border-l")}>
                    {fmtMonth(m)}
                  </th>
                ))}
                <th className={cn(TH, "border-l")}>Total</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.key} className="border-b">
                  <td className={NAME_TD}>{b.label}</td>
                  {fyMonths.map((m) => (
                    <td
                      key={m}
                      className={cn("border-l py-1 px-1 text-right", !editable && "py-2 px-2")}
                    >
                      {editable ? (
                        <GoalCell
                          brand={data.goalBrand}
                          platform={data.goalPlatform}
                          metricKey={b.key}
                          month={m}
                          initial={b.goals[m] ?? null}
                          onSaved={onSaved}
                        />
                      ) : (
                        <span className="tabular-nums">
                          {b.goals[m] == null ? "—" : fmtInt(b.goals[m] as number)}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className={cn(TD, "border-l font-semibold")}>{fmtInt(b.goalTotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className={NAME_TD}>Total</td>
                {fyMonths.map((m) => (
                  <td key={m} className={cn(TD, "border-l")}>
                    {fmtInt(buckets.reduce((s, b) => s + (b.goals[m] ?? 0), 0))}
                  </td>
                ))}
                <td className={cn(TD, "border-l")}>{fmtInt(data.totals.goalTotalFY)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Social breakdown ─────────────────────────────────────────────────
function SocialTable({ data }: { data: MonthlyLeadsResponse }) {
  const { fyMonths, q5Months, months, social } = data;
  const allCols = [...q5Months, ...fyMonths];
  return (
    <Card>
      <CardHeader>
        <CardTitle>📱 Social Media — Leads by Platform ({data.fiscalYearLabel})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className={NAME_TH}>Platform</th>
                {allCols.map((m) => (
                  <th key={m} className={cn(TH, m === fyMonths[0] && "border-l")}>
                    {fmtMonth(m)}
                  </th>
                ))}
                <th className={cn(TH, "border-l")}>Total</th>
                <th className={cn(TH, "border-l")}>Deals Closed</th>
                <th className={TH}>Close Rate</th>
              </tr>
            </thead>
            <tbody>
              {social.map((r) => (
                <tr
                  key={r.key}
                  className={cn("border-b", r.derived && "text-muted-foreground italic")}
                >
                  <td className={NAME_TD}>{r.label}</td>
                  {allCols.map((m) => (
                    <td key={m} className={cn(TD, m === fyMonths[0] && "border-l")}>
                      {fmtInt(r.actual[m] ?? 0)}
                    </td>
                  ))}
                  <td className={cn(TD, "border-l font-semibold")}>{fmtInt(r.leads)}</td>
                  <td className={cn(TD, "border-l")}>{fmtInt(r.dealsClosed)}</td>
                  <td className={TD}>{fmtPct(r.closeRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          “All TikTok” = FP TikTok + Diamond Dude TikTok. These platforms sum to the Social Media
          line in the tables above (spanning all {months.length} months).
        </p>
      </CardContent>
    </Card>
  );
}

// ── Campus opportunities (manual snapshot) ───────────────────────────
function CampusTable({ data }: { data: MonthlyLeadsResponse }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>🎓 Campus Opportunities</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Campus</th>
                <th className={TH}>2025</th>
                <th className={TH}>2026</th>
                <th className={TH}>Change</th>
              </tr>
            </thead>
            <tbody>
              {data.campus.map((c) => {
                const delta = c.y2026 - c.y2025;
                return (
                  <tr key={c.key} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium whitespace-nowrap">{c.campus}</td>
                    <td className={TD}>{fmtInt(c.y2025)}</td>
                    <td className={TD}>{fmtInt(c.y2026)}</td>
                    <td className={cn(TD, delta >= 0 ? "text-green-600" : "text-red-600")}>
                      {delta >= 0 ? "+" : ""}
                      {delta}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className="py-2 pr-4">Total</td>
                <td className={TD}>{fmtInt(data.campus.reduce((s, c) => s + c.y2025, 0))}</td>
                <td className={TD}>{fmtInt(data.campus.reduce((s, c) => s + c.y2026, 0))}</td>
                <td className={TD}>
                  {(() => {
                    const d =
                      data.campus.reduce((s, c) => s + c.y2026, 0) -
                      data.campus.reduce((s, c) => s + c.y2025, 0);
                    return `${d >= 0 ? "+" : ""}${d}`;
                  })()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Manual snapshot — HubSpot's campus field is free-text with many spellings, so these aren't
          auto-pulled like the source rows above.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Diamond Dude TikTok monthly goal strip ───────────────────────────
function TikTokGoalTable({ data, onSaved }: { data: MonthlyLeadsResponse; onSaved: () => void }) {
  const { fyMonths, ddTiktokGoals, editable } = data;
  const total = fyMonths.reduce((s, m) => s + (ddTiktokGoals[m] ?? 0), 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>💎 Diamond Dude TikTok — Monthly Goal ({data.fiscalYearLabel})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {fyMonths.map((m) => (
                <tr key={m} className="border-b last:border-0">
                  <td className="py-1.5 pr-4 font-medium whitespace-nowrap">{fmtMonth(m)}</td>
                  <td className="py-1 text-right">
                    {editable ? (
                      <GoalCell
                        brand={data.goalBrand}
                        platform={data.goalPlatform}
                        metricKey={data.ddTiktokGoalKey}
                        month={m}
                        initial={ddTiktokGoals[m] ?? null}
                        onSaved={onSaved}
                      />
                    ) : (
                      <span className="tabular-nums">
                        {ddTiktokGoals[m] == null ? "—" : fmtInt(ddTiktokGoals[m] as number)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className="py-2 pr-4">Total</td>
                <td className="py-2 text-right tabular-nums pr-2">{fmtInt(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
